import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { getDefaultWorkspace } from "../lib/workspace";
import { notifyProjectsChanged } from "../lib/useProjects";
import { ConfirmPermanentDelete } from "../components/ConfirmPermanentDelete";
import { ProjectDialog } from "../components/ProjectDialog";
import { deleteProjectPermanently } from "../lib/api";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { EmptyState } from "../ui/EmptyState";
import { Skeleton } from "../ui/Skeleton";
import { t, tVars } from "../i18n";

// Workspace-scoped projects registry. CRUD via modal dialog
// (same shape as Attendees + Glossary). Soft-delete via deleted_at;
// list view filters out deleted rows.
//
// A project groups attendees + glossary terms + meetings so they share
// context only when relevant. Meetings whose project is soft-deleted
// flow with workspace-global context only (context_builder.py treats
// them as if project_id were NULL). Per-project attendees + glossary
// stay on disk and reactivate on un-delete.

interface Project {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  deleted_at: string | null;
  created_at: string;
}

interface DialogState {
  mode: "create" | "edit";
  projectId: string | null;
  initial: { name: string; description: string };
}

export function Projects() {
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [rows, setRows] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [purgeTarget, setPurgeTarget] = useState<Project | null>(null);
  const [purging, setPurging] = useState(false);

  const loadRows = useCallback(
    async (ws: string, archived: boolean) => {
      setLoading(true);
      setError(null);
      let q = supabase
        .from("projects")
        .select("id, workspace_id, name, description, deleted_at, created_at")
        .eq("workspace_id", ws);
      q = archived ? q.not("deleted_at", "is", null) : q.is("deleted_at", null);
      const { data, error: e } = await q.order("name");
      if (e) setError(e.message);
      else setRows((data ?? []) as Project[]);
      setLoading(false);
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    getDefaultWorkspace()
      .then((ws) => {
        if (cancelled) return;
        setWorkspaceId(ws.id);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (workspaceId) loadRows(workspaceId, showArchived);
  }, [workspaceId, showArchived, loadRows]);

  function openCreate() {
    setDialog({ mode: "create", projectId: null, initial: { name: "", description: "" } });
  }

  function openEdit(row: Project) {
    setDialog({
      mode: "edit",
      projectId: row.id,
      initial: { name: row.name, description: row.description ?? "" },
    });
  }

  async function handleSaved(_id: string) {
    setDialog(null);
    notifyProjectsChanged();
    if (workspaceId) await loadRows(workspaceId, showArchived);
  }

  async function archive(row: Project) {
    const confirmed = window.confirm(
      tVars("projects.archiveConfirm", { name: row.name }),
    );
    if (!confirmed || !workspaceId) return;
    const prev = rows;
    setRows(rows.filter((r) => r.id !== row.id));
    const { error: e } = await supabase
      .from("projects")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", row.id);
    if (e) {
      setRows(prev);
      setError(tVars("projects.archiveFailed", { msg: e.message }));
    } else {
      notifyProjectsChanged();
    }
  }

  async function restore(row: Project) {
    if (!workspaceId) return;
    const prev = rows;
    setRows(rows.filter((r) => r.id !== row.id));
    const { error: e } = await supabase
      .from("projects")
      .update({ deleted_at: null })
      .eq("id", row.id);
    if (e) {
      setRows(prev);
      setError(tVars("projects.restoreFailed", { msg: e.message }));
    } else {
      notifyProjectsChanged();
    }
  }

  async function confirmPurge() {
    if (!purgeTarget) return;
    const id = purgeTarget.id;
    setPurging(true);
    try {
      await deleteProjectPermanently(id);
      setRows((prev) => prev.filter((r) => r.id !== id));
      setPurgeTarget(null);
      notifyProjectsChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPurging(false);
    }
  }

  return (
    <>
      {/* Header row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <h2 style={{ margin: 0 }}>
          {showArchived ? t("projects.headingArchived") : t("projects.heading")}
          {rows.length > 0 && (
            <span
              style={{
                fontSize: 14,
                fontWeight: 400,
                color: "var(--ink-3)",
                marginInlineStart: 8,
              }}
            >
              ({rows.length})
            </span>
          )}
        </h2>
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowArchived((v) => !v)}
          >
            {showArchived ? t("projects.backToProjects") : t("projects.archived")}
          </Button>
          {!showArchived && (
            <Button
              variant="primary"
              size="sm"
              icon="plus"
              onClick={openCreate}
              disabled={!workspaceId}
            >
              {t("projects.addProject")}
            </Button>
          )}
        </div>
      </div>

      {error && (
        <p className="error" style={{ marginBottom: 12 }}>
          {error}
        </p>
      )}

      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {[1, 2, 3].map((i) => (
            <div key={i} className="card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
                <Skeleton height={16} width="35%" />
                <Skeleton height={13} width="55%" />
              </div>
              <Skeleton height={30} width={80} style={{ borderRadius: 6 }} />
            </div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={showArchived ? "archive" : "folder"}
          title={showArchived ? t("projects.emptyArchivedTitle") : t("projects.emptyTitle")}
          description={
            showArchived
              ? t("projects.emptyArchivedDesc")
              : t("projects.emptyDesc")
          }
          action={
            !showArchived ? (
              <Button
                variant="primary"
                size="sm"
                icon="plus"
                onClick={openCreate}
                disabled={!workspaceId}
              >
                Add project
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <table className="meetings">
            <thead>
              <tr>
                <th>{t("common.nameLabel")}</th>
                <th>{t("projects.descriptionLabel")}</th>
                <th style={{ width: 200 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <Link to={`/projects/${row.id}`}>
                      <span dir="auto">
                        <strong>{row.name}</strong>
                      </span>
                    </Link>
                  </td>
                  <td
                    style={{ fontSize: 13, color: "var(--ink-3)" }}
                  >
                    <span dir="auto">{row.description || ""}</span>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      {showArchived ? (
                        <>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => restore(row)}
                          >
                            {t("common.restore")}
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => setPurgeTarget(row)}
                          >
                            {t("library.deletePermanently")}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => openEdit(row)}
                          >
                            {t("common.edit")}
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            icon="archive"
                            onClick={() => archive(row)}
                          >
                            {t("common.archive")}
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <ConfirmPermanentDelete
        open={purgeTarget !== null}
        itemKind="project"
        itemName={purgeTarget?.name ?? ""}
        busy={purging}
        onConfirm={confirmPurge}
        onCancel={() => {
          if (!purging) setPurgeTarget(null);
        }}
      />

      <ProjectDialog
        open={dialog !== null}
        mode={dialog?.mode ?? "create"}
        initial={dialog?.initial}
        workspaceId={workspaceId}
        projectId={dialog?.projectId}
        onClose={() => setDialog(null)}
        onSaved={handleSaved}
      />
    </>
  );
}
